<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  modelValue: string[];
  items: Array<{ value: string; title: string }>;
  label: string;
  variant?: 'underlined' | 'outlined' | 'plain' | 'solo' | 'filled';
  density?: 'default' | 'comfortable' | 'compact';
  maxVisibleChips?: number;
  hideDetails?: boolean | 'auto';
}

interface Emits {
  (e: 'update:modelValue', value: string[]): void;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'underlined',
  density: undefined,
  maxVisibleChips: 1,
  hideDetails: false,
});

const emit = defineEmits<Emits>();

const internalValue = computed({
  get: () => props.modelValue,
  set: (value: string[]) => emit('update:modelValue', value),
});

const selectedCount = computed(() => props.modelValue?.length ?? 0);

function removeItem(value: string) {
  emit('update:modelValue', props.modelValue.filter(v => v !== value));
}
</script>

<template>
  <v-autocomplete
    v-model="internalValue"
    :items="items"
    :label="label"
    :density="density"
    :variant="variant"
    :hide-details="hideDetails"
    multiple
    clearable
    single-line
  >
    <template #selection="{ item, index }">
      <v-chip
        v-if="index < maxVisibleChips"
        closable
        size="small"
        @click:close.stop="removeItem(item.value as string)"
      >
        {{ item.title }}
      </v-chip>
      <span
        v-else-if="index === maxVisibleChips"
        class="text-caption text-medium-emphasis ml-1"
      >
        +{{ selectedCount - maxVisibleChips }}
      </span>
    </template>
  </v-autocomplete>
</template>
